/* Component in the Projects Component to display a Project's data */
import React, { useEffect, useState } from "react";

import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { useHistory } from "react-router-dom";
import toast from "react-hot-toast";
import { Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import { axiosInstance } from "../../api/axiosDefaults";
import { UniDropdown } from "../../components/UniDropDown";
import { hasBudgetPlan } from "../../utils/hasBudgetPlan";
import useActivePlan from "../../hooks/useActivePlan";
import { hasProjectPlan } from "../../utils/hasProjectPlan";

import styles from "../../styles/Account.module.css";

const Project = ({
  id,
  name,
  url,
  stripe_id,
  company,
  owner,
  shotcaller_url,
  category_type,
  payment,
  created_at,
  fetchData,
  budget_id
}) => {
  const currentUser = useCurrentUser();
  const { currentlyActivePlans } = useActivePlan();
  const is_owner = currentUser?.username === owner;
  const history = useHistory();
  const [sharedListing, setSharedListing] = useState([]);
  const [show, setShow] = useState(false);
  const [isLoadingSharedInfo, setIsLoadingSharedInfo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [modalType, setModalType] = useState("add");
  const [trigger, setTrigger] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
  });

  const handleEdit = () => {
    history.push(`/projects/edit/${id}`);
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/projects-detail/${id}`);
      console.log("delete");
      console.log(currentUser);
      fetchData();
      // /projects-detail/:projectId
    } catch (err) {
      console.log("failed to delete", err);
    }
  };

  const getTargetBudgetId = () => budget_id;

  const handleClose = () => {
    setShow(false);
    setSuccessMsg("");
    setFormData({ name: "", email: "" });
  };

  const handleShow = async (type) => {
    const targetBudgetId = getTargetBudgetId();
    if (!targetBudgetId) {
      toast.error("Please create budget first.");
      return;
    }
    setModalType(type);
    if (type === "list") {
      try {
        setIsLoadingSharedInfo(true);
        const ok = await handleFetchSharedListing();
        if (ok) setShow(true);
      } finally {
        setIsLoadingSharedInfo(false);
      }
      return;
    }
    setShow(true);
  };

  const handleChange = (e) => {
    const { name: fieldName, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const targetBudgetId = getTargetBudgetId();
    if (!targetBudgetId) {
      toast.error("Please create budget first.");
      return;
    }

    setLoading(true);
    setSuccessMsg("");

    try {
      const res = await axiosInstance.post(
        "/budget-sharing/",
        {
          budget: targetBudgetId,
          email: formData.email,
          name: formData.name,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      setTrigger(true);
      if (String(res?.data?.status) === "201") {
        setSuccessMsg("Budget shared successfully!");
        setFormData({ name: "", email: "" });
      } else {
        toast.error(
          res?.error?.response?.data?.non_field_errors?.[0] ||
          "Something went wrong"
        );
      }
    } catch (err) {
      setSuccessMsg("Failed to share. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFetchSharedListing = async () => {
    const targetBudgetId = getTargetBudgetId();
    if (!targetBudgetId) return false;
    try {
      const res = await axiosInstance.get(
        `/budget-sharing/?budget_id=${targetBudgetId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      if (!res?.success) {
        setSharedListing([]);
        throw res.error;
      }

      // axiosInstance interceptor wraps axios response as { success, data: axiosResponse }
      // The API payload shape here is { results: [...] }
      setSharedListing(res?.data?.data?.results || []);
      return true;
    } catch (e) {
      setSharedListing([]);
      const status = e?.response?.status;
      if (status && status !== 401 && status !== 440) {
        toast.error("Unable to load shared budget info. Please try again.");
      }
      return false;
    }
  };

  const handleDeleteSharedBudget = async (sharedId) => {
    const res = await axiosInstance.delete(`/budget-sharing/${sharedId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
    });
    if (res?.success) {
      setTrigger(true);
      toast.success("Budget deleted successfully!");
      return;
    }
    const status = res?.error?.response?.status;
    if (status && status !== 401 && status !== 440) {
      toast.error("Unable to delete shared budget. Please try again.");
    }
  };

  useEffect(() => {
    if (trigger) {
      handleFetchSharedListing();
      setTrigger(false);
    }
  }, [trigger]);

  return (
    <div>
      <Card className="mb-3">
        <Card.Body className={`${styles.ProTop} py-2`}>
          <Row className="d-flex align-items-center">
            <Col xs={2}></Col>
            <Col className="text-center" xs={8}>
              <h5
                style={{
                  fontWeight: "500",
                  color: "#fff",
                  textTransform: "capitalize",
                }}
                className="ml-1 ml-md-3"
              >
                {" "}
                {name}{" "}
              </h5>
            </Col>
            <Col
              xs={2}
              className={`d-flex align-items-center justify-content-center ${styles.ProjectDropdownMenu}`}
            >
              {is_owner && (
                <UniDropdown
                  handleEdit={handleEdit}
                  handleDelete={handleDelete}
                  id={id}
                />
              )}
            </Col>
          </Row>
        </Card.Body>
        {/* end new */}
        <Card.Body className="py-0">
          {company && (
            <p
              style={{ fontStyle: "italic" }}
              className="mb-0 mt-2 pb-0 text-center"
            >
              {company}
            </p>
          )}
          <hr className="my-2" />
          {/* <Card.Text>Project Id: {id}</Card.Text> */}
          {/* <Card.Text>Stripe Id: {stripe_id}</Card.Text> */}
          <Card.Text>
            Project URL:{" "}
            <a
              href={shotcaller_url}
              style={{ textDecoration: "underline" }}
              target="_blank"
              rel="noreferrer"
            >
              {shotcaller_url}
            </a>
          </Card.Text>
          <Card.Text className="d-flex">
            Project Type: <span className="ml-2 d-flex">{category_type}</span>
          </Card.Text>
          <Card.Text className="d-flex">
            Created On: <span className="ml-2 d-flex">{created_at}</span>
          </Card.Text>
          {/* <Card.Text className="d-flex">
            Amount: <span className="ml-2 d-flex">${payment?.amount}</span>
          </Card.Text> */}
          {/* <Card.Text className="d-flex">
            Payment Date: <span className="ml-2 d-flex">{created_at}</span>
          </Card.Text> */}
          {/* <Card.Text className="d-flex">
            Payment Status: <span className="ml-2 d-flex">Success</span>
          </Card.Text> */}
          {/* <Card.Text className="d-flex">
            Stripe Transaction Id:{" "}
            <span className="ml-2 d-flex">{payment?.stripe_id}</span>
          </Card.Text> */}
          <Row className="mt-1">
            <Col>
              <div
                className={`d-flex flex-wrap justify-content-center ${styles.ProjectActionsWrap}`}
              >
                <Button
                  className={`${styles.ProjectActionButton} mx-1 my-1`}
                  onClick={() => {
                    if (
                      hasBudgetPlan(currentlyActivePlans) ||
                      hasProjectPlan(currentlyActivePlans)
                    ) {
                      if (budget_id === null) {
                        history.push(`/budgets/create?id=${id}`);
                      } else {
                        history.push(`/${id}/budgets`);
                      }
                    } else {
                      toast.error(
                        "Cannot access budget. Please buy either budget or project subscription"
                      );
                    }
                  }}
                >
                  Budget
                </Button>
                <Button
                  className={`${styles.ProjectActionButton} mx-1 my-1`}
                  onClick={() => handleShow("add")}
                >
                  <i className="fa fa-share"></i> Share Budget
                </Button>
                <Button
                  className={`${styles.ProjectActionButton} mx-1 my-1`}
                  onClick={() => handleShow("list")}
                  disabled={isLoadingSharedInfo}
                >
                  <i className="fa fa-info"></i>{isLoadingSharedInfo ? "Loading..." : "Shared Budget Info"}{" "}
                  {isLoadingSharedInfo && (
                    <Spinner
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="ml-2"
                    />
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
        <hr />
        <Modal show={show} onHide={handleClose} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>Share Budget</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {modalType === "add" && (
              <Form onSubmit={handleSubmit}>
                <Form.Group controlId={`formName-${id}`} className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter full name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group controlId={`formEmail-${id}`} className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter recipient's email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                {successMsg && <div className="text-success mb-3">{successMsg}</div>}

                <div className="d-flex justify-content-end">
                  <Button
                    variant="secondary"
                    onClick={handleClose}
                    className="me-2"
                  >
                    Cancel
                  </Button>
                  &nbsp;
                  <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? "Sharing..." : "Share"}
                  </Button>
                </div>
              </Form>
            )}
            {modalType === "list" && (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>SL No.</th>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Public URL</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {sharedListing?.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">
                        No Shared Budget List Found!
                      </td>
                    </tr>
                  )}
                  {sharedListing?.map((res, i) => {
                    const { id: sharedId, email, url: sharedUrl, name: sharedName } = res;
                    return (
                      <tr key={sharedId}>
                        <td>{i + 1}</td>
                        <td>{email}</td>
                        <td>{sharedName}</td>
                        <td>
                          <a href={sharedUrl} target="_blank" rel="noopener noreferrer">
                            Open Link
                          </a>
                        </td>
                        <td>
                          <Button
                            size="sm"
                            onClick={() => handleDeleteSharedBudget(sharedId)}
                            className="bg-danger btn btn-sm"
                            rel="noopener noreferrer"
                          >
                            <i className="fa fa-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Modal.Body>
        </Modal>
      </Card>
    </div>
  );
};

export default Project;
